import { DialogContent, DialogTitle, Stack, Typography } from '@mui/material';
import { useEffect, useState } from 'react';
import {
  SaveButton,
  TextInput,
  SimpleForm,
  useDataProvider,
  useNotify,
  useRefresh,
  NumberInput,
  SelectInput,
  DateTimeInput,
  ArrayInput,
  SimpleFormIterator,
  useTranslate,
} from 'react-admin';
import dayjs from 'dayjs';
import { getCategoryChoices } from '../../categories/service';

export default function CreateTask({ onClose }: { onClose: () => void }) {
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const refresh = useRefresh();
  const translate = useTranslate();
  const [saving, setSaving] = useState(false);
  const [categoryChoices, setCategoryChoices] = useState<Array<{ id: number | string; name: string }>>([]);
  const frequencyChoices = [
    { id: 'DAILY', name: 'DAILY' },
    { id: 'ONE_TIME', name: 'ONE_TIME' },
    { id: 'UNLIMITED', name: 'UNLIMITED' },
    { id: 'PROGRESSIVE', name: 'PROGRESSIVE' },
    { id: 'MANUAL', name: 'MANUAL' },
  ];
  const taskIdentifierChoices = [
    { id: 'DAILY_CHECKIN', name: 'DAILY_CHECKIN' },
    { id: 'MEME_TRADE_DAILY', name: 'MEME_TRADE_DAILY' },
    { id: 'PERPETUAL_TRADE_DAILY', name: 'PERPETUAL_TRADE_DAILY' },
    { id: 'MARKET_PAGE_VIEW', name: 'MARKET_PAGE_VIEW' },
    { id: 'CHECK_MARKET_TRENDS', name: 'CHECK_MARKET_TRENDS' },
    { id: 'TWITTER_FOLLOW', name: 'TWITTER_FOLLOW' },
    { id: 'TWITTER_RETWEET', name: 'TWITTER_RETWEET' },
    { id: 'TWITTER_LIKE', name: 'TWITTER_LIKE' },
    { id: 'TELEGRAM_JOIN', name: 'TELEGRAM_JOIN' },
    { id: 'INVITE_FRIENDS', name: 'INVITE_FRIENDS' },
    { id: 'SHARE_REFERRAL', name: 'SHARE_REFERRAL' },
    { id: 'SHARE_EARNINGS_CHART', name: 'SHARE_EARNINGS_CHART' },
    { id: 'TRADING_POINTS', name: 'TRADING_POINTS' },
  ];
  const resetPeriodChoices = [
    { id: 'DAILY', name: 'DAILY' },
    { id: 'WEEKLY', name: 'WEEKLY' },
    { id: 'MONTHLY', name: 'MONTHLY' },
    { id: 'NEVER', name: 'NEVER' },
  ];
  const verificationMethodChoices = [
    { id: 'AUTO', name: 'AUTO' },
    { id: 'MANUAL', name: 'MANUAL' },
    { id: 'CLICK_VERIFY', name: 'CLICK_VERIFY' },
  ];

  useEffect(() => {
    getCategoryChoices().then(setCategoryChoices).catch(() => setCategoryChoices([]));
  }, []);

  const onSubmit = async (values: any) => {
    setSaving(true);
    try {
      let conditionsString = '{}';
      if (Array.isArray(values.conditions)) {
        const obj = values.conditions.reduce((acc: any, cur: any) => {
          if (cur && cur.key) acc[cur.key] = cur.value;
          return acc;
        }, {} as Record<string, any>);
        try {
          conditionsString = JSON.stringify(obj);
        } catch {
          conditionsString = '{}';
        }
      } else if (typeof values.conditions === 'string') {
        const trimmed = values.conditions?.trim();
        if (trimmed) {
          try {
            JSON.parse(trimmed);
            conditionsString = trimmed;
          } catch {
            conditionsString = JSON.stringify({ value: trimmed });
          }
        }
      } else if (values.conditions && typeof values.conditions === 'object') {
        try {
          conditionsString = JSON.stringify(values.conditions);
        } catch {
          conditionsString = '{}';
        }
      }

      const processedValues = {
        ...values,
        name: typeof values.name === 'object' ? values.name : { en: values.name || '' },
        points: Number(values.points) || 0,
        isActive: Boolean(values.isActive),
        startDate: values.startDate && values.startDate !== '' ? dayjs(values.startDate).valueOf() : null,
        endDate: values.endDate && values.endDate !== '' ? dayjs(values.endDate).valueOf() : null,
        conditions: conditionsString,
      };

      await dataProvider.create('activity-task', { data: processedValues });
      notify(translate('activity.tasks.createdSuccessfully'), { type: 'success' });
      refresh();
      onClose();
    } catch (error) {
      console.error('Create error:', error);
      notify(translate('activity.tasks.failedToCreate'), { type: 'error' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <DialogTitle>{translate('activity.common.createTask')}</DialogTitle>
      <DialogContent>
        <SimpleForm onSubmit={onSubmit} toolbar={<SaveButton alwaysEnable disabled={saving} />}>
          <Stack spacing={2} sx={{ mt: 2 }}>
            <Typography variant="h6">{translate('activity.common.basicInformation')}</Typography>
            <SelectInput
              source="categoryId"
              label={translate('activity.common.category')}
              fullWidth
              required
              choices={categoryChoices}
            />

            <Typography variant="h6" sx={{ mt: 2 }}>
              {translate('activity.common.nameMultiLanguage')}
            </Typography>

            <TextInput source="name.en" label={translate('activity.name.en')} fullWidth required />
            <TextInput source="name.zh" label={translate('activity.name.zh')} fullWidth />
            <TextInput source="name.ja" label={translate('activity.name.ja')} fullWidth />
            <TextInput source="name.hk" label={translate('activity.name.hk')} fullWidth />
            <TextInput source="name.vi" label={translate('activity.name.vi')} fullWidth />
            <TextInput source="name.hi" label={translate('activity.name.hi')} fullWidth />

            <Typography variant="h6" sx={{ mt: 2 }}>
              {translate('activity.common.additionalFields')}
            </Typography>

            <TextInput source="description" label={translate('activity.common.description')} fullWidth multiline rows={3} />

            <SelectInput
              source="frequency"
              label={translate('activity.tasks.frequency')}
              fullWidth
              required
              choices={frequencyChoices}
            />

            <SelectInput
              source="taskIdentifier"
              label={translate('activity.tasks.taskIdentifier')}
              fullWidth
              choices={taskIdentifierChoices}
            />

            <NumberInput source="points" label={translate('activity.common.points')} fullWidth required defaultValue={1} />

            <TextInput source="maxCompletions" label={translate('activity.tasks.maxCompletions')} fullWidth />

            <SelectInput
              source="resetPeriod"
              label={translate('activity.tasks.resetPeriod')}
              fullWidth
              choices={resetPeriodChoices}
            />

            <Typography variant="body2">{translate('activity.common.conditions')}</Typography>

            <NumberInput source="conditions.min_trading_volume" label={translate('activity.tasks.minTradingVolume')} fullWidth />
            <NumberInput source="conditions.required_trade_count" label={translate('activity.tasks.requiredTradeCount')} fullWidth />
            <NumberInput source="conditions.consecutive_days" label={translate('activity.tasks.consecutiveDays')} fullWidth />
            <TextInput source="conditions.target_page" label={translate('activity.tasks.targetPage')} fullWidth />
            <TextInput source="conditions.social_media_action" label={translate('activity.tasks.socialMediaAction')} fullWidth />
            <NumberInput source="conditions.referral_count" label={translate('activity.tasks.referralCount')} fullWidth />

            <Typography variant="body2">{translate('activity.common.consecutiveCheckinMilestones')}</Typography>
            <ArrayInput source="conditions.consecutive_checkin_milestones" label="" fullWidth>
              <SimpleFormIterator>
                <NumberInput source="days" label={translate('activity.common.days')} />
                <NumberInput source="points" label={translate('activity.common.points')} />
                <TextInput source="name.en" label={translate('activity.name.nameEn')} />
                <TextInput source="name.zh" label={translate('activity.name.nameZh')} />
                <TextInput source="name.ja" label={translate('activity.name.nameJa')} />
                <TextInput source="name.hi" label={translate('activity.name.nameHi')} />
                <TextInput source="name.hk" label={translate('activity.name.nameHk')} />
                <TextInput source="name.vi" label={translate('activity.name.nameVi')} />
                <TextInput source="task_icon" label={translate('activity.tasks.taskIcon')} />
              </SimpleFormIterator>
            </ArrayInput>

            <Stack direction="row" alignItems="center" spacing={2}>
              <TextInput source="actionTarget" label={translate('activity.tasks.actionTarget')} fullWidth />
              <Typography variant="body2" color="text.secondary">
                *AccumulatedTradingTasks with actionTarget = memeTrade and frequency = PROGRESSIVE
              </Typography>
            </Stack>

            <SelectInput
              source="verificationMethod"
              label={translate('activity.tasks.verificationMethod')}
              fullWidth
              choices={verificationMethodChoices}
            />

            <TextInput source="externalLink" label={translate('activity.tasks.externalLink')} fullWidth />
            <TextInput source="taskIcon" label={translate('activity.tasks.taskIcon')} fullWidth />
            <TextInput source="buttonText" label={translate('activity.tasks.buttonText')} fullWidth />
            <DateTimeInput source="startDate" label={translate('activity.common.startDate')} fullWidth />
            <DateTimeInput source="endDate" label={translate('activity.common.endDate')} fullWidth />
            <NumberInput source="sortOrder" label={translate('activity.common.sortOrder')} fullWidth defaultValue={0} />
          </Stack>
        </SimpleForm>
      </DialogContent>
    </>
  );
}
